import { useOrganization } from '@library/react-toolkit'
import { either, task } from 'fp-ts'
import { pipe } from 'fp-ts/lib/function'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useShowError } from '../Notifications'
import {
  createDiscount,
  deleteDiscount,
  getDiscount,
  getDiscounts,
  updateDiscount,
} from './api'
import {
  Discount,
  DiscountsSearchRequest,
  NewDiscount,
  Nullable,
} from './types'

export const useDiscounts = (
  req: Partial<Nullable<DiscountsSearchRequest>>
) => {
  const org = useOrganization()
  const showError = useShowError()

  return useQuery(
    [`${org?.name}-discounts`, req],
    pipe(
      getDiscounts({ ...req, organizationName: org?.name ?? '' }),
      task.map(
        either.fold(
          e => {
            throw e
          },
          x => x
        )
      )
    ),
    {
      enabled: !!org?.name,
      keepPreviousData: true,
      staleTime: 60 * 1000,
      onError: () => showError('getDiscountsError'),
    }
  )
}

export const useDiscount = (id: number | undefined) => {
  const org = useOrganization()
  const showError = useShowError()

  return useQuery(
    [`${org?.name}-tax`, id],
    pipe(
      getDiscount(org?.name ?? '', id ?? 0),
      task.map(
        either.fold(
          e => {
            throw e
          },
          x => x
        )
      )
    ),
    {
      enabled: !!org?.name && !!id,
      staleTime: 60 * 1000,
      onError: () => {
        showError('getDiscountsError')
      },
    }
  )
}

export const useAddDiscount = () => {
  const queryClient = useQueryClient()
  const org = useOrganization()
  const showError = useShowError()

  return useMutation(
    (discount: NewDiscount) =>
      pipe(
        createDiscount({ organization: org?.name ?? '', ...discount }),
        task.map(
          either.fold(
            e => {
              throw e
            },
            x => x
          )
        )
      )(),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(`${org?.name}-discounts`)
      },
      onError: () => showError('addDiscountError'),
    }
  )
}

export const useDeleteDiscount = () => {
  const queryClient = useQueryClient()
  const org = useOrganization()
  const showError = useShowError()

  return useMutation(
    (id: number) =>
      pipe(
        deleteDiscount(org?.name ?? '', id),
        task.map(
          either.fold(
            e => {
              throw e
            },
            x => x
          )
        )
      )(),
    {
      onSuccess: (_, id) => {
        queryClient.invalidateQueries(`${org?.name}-discounts`)
        queryClient.invalidateQueries([`${org?.name}-discount`, id])
      },
      onError: () => showError('deleteDiscountError'),
    }
  )
}

export const useUpdateDiscount = () => {
  const queryClient = useQueryClient()
  const org = useOrganization()
  const showError = useShowError()

  return useMutation(
    (discount: Discount) =>
      pipe(
        updateDiscount({ organization: org?.name ?? '', ...discount }),
        task.map(
          either.fold(
            e => {
              throw e
            },
            x => x
          )
        )
      )(),
    {
      onSuccess: (_, id) => {
        queryClient.invalidateQueries(`${org?.name}-discounts`)
        queryClient.invalidateQueries([`${org?.name}-discount`, id])
      },
      onError: () => showError('editDiscountError'),
    }
  )
}
